import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, History, Loader2 } from 'lucide-react';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

function trimLabel(value, maxLength = 72) {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1).trim()}…` : clean;
}

function formatRelativeTime(value) {
  const timestamp = Date.parse(value || '');
  if (!Number.isFinite(timestamp)) return '';
  const diff = Date.now() - timestamp;
  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.round(diff / 60000))}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.max(1, Math.round(diff / 3600000))}h ago`;
  return `${Math.max(1, Math.round(diff / 86400000))}d ago`;
}

function buildItems(feature, records = []) {
  if (feature === 'chat') {
    return records.map((chat) => {
      const params = new URLSearchParams();
      if (chat.workspace_id) params.set('workspace_id', chat.workspace_id);
      if (chat.matter_id) {
        params.set('matter_id', chat.matter_id);
        params.set('matter_name', chat.matter_name || chat.matter_id);
      }
      return {
        id: chat.chat_id,
        href: `/chat/${chat.chat_id}${params.toString() ? `?${params.toString()}` : ''}`,
        title: trimLabel(chat.title || chat.matter_name || 'Chat thread'),
        meta: trimLabel(chat.matter_name || chat.mode || 'Conversation'),
        updatedAt: chat.updated_at,
      };
    });
  }
  if (feature === 'research') {
    return records.map((workspace) => ({
      id: workspace.workspace_id,
      href: `/research?workspace_id=${workspace.workspace_id}`,
      title: trimLabel(workspace.title || workspace.master_document_title || 'Research workspace'),
      meta: trimLabel(workspace.matter_context?.name || workspace.activity_type || 'Research workspace'),
      updatedAt: workspace.updated_at,
    }));
  }
  if (feature === 'sabha') {
    return records.map((matter) => ({
      id: matter.matter_id,
      href: `/war-room/${matter.matter_id}`,
      title: trimLabel(matter.title || matter.name || 'Matter workspace'),
      meta: trimLabel(matter.court_forum || matter.case_number || 'Matter'),
      updatedAt: matter.updated_at || matter.latest_activity_at,
    }));
  }
  if (feature === 'aina') {
    return records.map((thread) => {
      const params = new URLSearchParams();
      params.set('thread_id', thread.thread_id);
      if (thread.run_id) params.set('run_id', thread.run_id);
      const base = thread.matter_id ? `/aina/${thread.matter_id}` : '/aina';
      return {
        id: thread.thread_id,
        href: `${base}?${params.toString()}`,
        title: trimLabel(thread.title || 'Aina conversation'),
        meta: trimLabel(thread.matter_name || thread.run_id || 'Aina'),
        updatedAt: thread.updated_at,
      };
    });
  }
  if (feature === 'jolly') {
    return records.map((session) => ({
      id: session.session_id,
      href: `/jolly?session_id=${session.session_id}`,
      title: trimLabel(session.title || session.matter_name || 'Jolly session'),
      meta: trimLabel(session.matter_name || session.mode || 'Voice session'),
      updatedAt: session.updated_at,
    }));
  }
  if (feature === 'case') {
    return records.map((record) => ({
      id: record.matter_id,
      href: `/case/${record.matter_id}`,
      title: trimLabel(record.title || 'Case workspace'),
      meta: trimLabel(record.history_meta || record.summary || record.origin || 'Case'),
      updatedAt: record.last_opened_at || record.last_saved_at || record.last_autosaved_at || record.updated_at,
    }));
  }
  return [];
}

export default function FeatureHistoryMenu({
  feature,
  matterId = '',
  workspaceId = '',
  label = 'History',
  buttonClassName = 'app-chat-button',
  activeId = '',
  onSelect = null,
  dropdownAlign = 'left',
}) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    let url = '';
    if (feature === 'chat') {
      url = `${API}/chats`;
    } else if (feature === 'research') {
      const params = new URLSearchParams({ last_agent: 'RESEARCH_AGENT', limit: '10' });
      if (matterId) params.set('matter_id', matterId);
      url = `${API}/agent-workspaces?${params.toString()}`;
    } else if (feature === 'sabha') {
      url = `${API}/matters`;
    } else if (feature === 'aina') {
      const params = new URLSearchParams();
      if (matterId) params.set('matter_id', matterId);
      if (workspaceId) params.set('workspace_id', workspaceId);
      url = `${API}/aina/threads${params.toString() ? `?${params.toString()}` : ''}`;
    } else if (feature === 'jolly') {
      const params = new URLSearchParams();
      if (matterId) params.set('matter_id', matterId);
      url = `${API}/jolly/sessions${params.toString() ? `?${params.toString()}` : ''}`;
    } else if (feature === 'case') {
      url = `${API}/cases`;
    }
    if (!url) return undefined;

    setLoading(true);
    fetch(url, {
      headers: getAuthHeaders(),
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (resp) => (resp.ok ? resp.json() : []))
      .then((payload) => {
        const raw = Array.isArray(payload) ? payload : [];
        let filtered = raw;
        if (feature === 'chat' && matterId) {
          filtered = raw.filter((chat) => chat.matter_id === matterId || chat.workspace_id === workspaceId);
        }
        setItems(buildItems(feature, filtered).slice(0, 10));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [feature, getAuthHeaders, matterId, open, workspaceId]);

  const buttonLabel = useMemo(() => {
    if (items.length > 0) return `${label} (${items.length})`;
    return label;
  }, [items.length, label]);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <History size={13} />}
        {buttonLabel}
        <ChevronDown size={12} style={{ opacity: 0.72 }} />
      </button>
      {open ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            ...(dropdownAlign === 'right' ? { right: 0 } : { left: 0 }),
            width: 'min(380px, 82vw)',
            maxHeight: '360px',
            overflowY: 'auto',
            zIndex: 120,
            borderRadius: '16px',
            border: '1px solid color-mix(in srgb, var(--border-default) 55%, transparent)',
            background: 'color-mix(in srgb, var(--bg-surface) 66%, transparent)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            boxShadow: '0 14px 26px rgba(15, 23, 42, 0.08)',
            padding: '8px',
          }}
        >
          {items.length === 0 ? (
            <div style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
              No recent history for this feature yet.
            </div>
          ) : items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={async () => {
                if (onSelect) {
                  const handled = await onSelect(item);
                  if (handled === false) return;
                } else {
                  navigate(item.href);
                }
                setOpen(false);
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: item.id === activeId ? 'rgba(15,118,110,0.08)' : 'transparent',
                padding: '10px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, lineHeight: 1.45 }}>
                {item.title}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.45 }}>
                  {item.meta}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                  {formatRelativeTime(item.updatedAt)}
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
